import { ChevronLeft, Save, Settings2 } from "lucide-react";
import { type PropsWithChildren, type ReactNode } from "react";
import { useIntl } from "react-intl";

import { cn } from "../../../../utils";
import { Icon } from "../../../atoms/icon";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "../../../molecules/dropdown-menu";
import { useDataTableContext } from "../../context";
import { useDataTableSettingsContext } from "../../context/data-table-settings-context";
import { useBrowseDataQuery } from "../../hooks/browse-data-query";
import { AddDataTablePreferenceAction } from "./actions/add-data-table-preference";
import { DataTablePreferencesEvidence } from "./evidence";
import { useApplyPreferenceProfile } from "./hooks/apply-preference-profile";
import { useGetProfileFromPreferences } from "./hooks/get-profile-from-preference";
import { dataTablePreferencesBrowseSchema } from "./schema";
import {
  createDataTablePreferencesApi,
  parsePreferenceSettings,
  viewPreferencesKey,
} from "./utils";

function Preferences() {
  const intl = useIntl();

  const { setShowAddPreference, setShowManagePreferences } =
    useDataTableSettingsContext();

  const table = useDataTableContext();

  const profile = useGetProfileFromPreferences();

  const applyPreferenceProfile = useApplyPreferenceProfile();

  const { data, isLoading } = useBrowseDataQuery({
    id: table.tableId,
    queryKey: viewPreferencesKey(table.tableId),
    api: createDataTablePreferencesApi(table.api),
    schema: dataTablePreferencesBrowseSchema,
    options: {
      filters: [
        {
          field: "tableId",
          operation: "EQ",
          value: table.tableId,
        },
      ],
      sorts: [{ field: "createdAt", asc: false, type: "FIELD" }],
      offset: 0,
      size: 10,
    },
  });

  return (
    <>
      <DropdownMenuSub>
        <DropdownMenuSubTrigger asChild={true}>
          <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
            <Icon Icon={ChevronLeft} className="mr-2 size-4" />

            {intl.formatMessage({
              id: "data-table.preferences.select-profile",
              defaultMessage: "Vybrat nastavení",
            })}
          </DropdownMenuItem>
        </DropdownMenuSubTrigger>

        <DropdownMenuPortal>
          <DropdownMenuSubContent className="max-w-64">
            {data && data.items.length > 0 ? (
              data.items.map((item) => (
                <DropdownMenuItem
                  key={item.id}
                  className={cn({ "bg-primary-200": profile?.id === item.id })}
                  onSelect={() => {
                    if (item.settings) {
                      const settings = parsePreferenceSettings(item.settings);

                      void applyPreferenceProfile(
                        { id: item.id, name: item.name },
                        settings,
                      );
                    }
                  }}
                >
                  {item.name}
                </DropdownMenuItem>
              ))
            ) : isLoading ? (
              <DropdownMenuItem
                disabled={true}
                className="data-[disabled]:opacity-100"
              >
                {intl.formatMessage({
                  id: "data-table.preferences.loading",
                  defaultMessage: "Načítání...",
                })}
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem
                disabled={true}
                className="data-[disabled]:opacity-100"
              >
                {intl.formatMessage({
                  id: "data-table.preferences.no-data",
                  defaultMessage: "Žádná data",
                })}
              </DropdownMenuItem>
            )}

            <DropdownMenuSeparator />

            <DropdownMenuItem onSelect={() => setShowManagePreferences(true)}>
              <Icon Icon={Settings2} className="mr-2 size-4" />

              {intl.formatMessage({
                id: "data-table.preferences.manage",
                defaultMessage: "Spravovat nastavení",
              })}
            </DropdownMenuItem>
          </DropdownMenuSubContent>
        </DropdownMenuPortal>
      </DropdownMenuSub>

      <DropdownMenuItem onSelect={() => setShowAddPreference(true)}>
        <Icon Icon={Save} className="mr-2 size-4" />

        {intl.formatMessage({
          id: "data-table.preferences.save-profile",
          defaultMessage: "Uložit nastavení",
        })}
      </DropdownMenuItem>
    </>
  );
}

export function DataTablePreferences({
  trigger,
  children,
}: PropsWithChildren<{
  trigger: ReactNode;
}>) {
  const table = useDataTableContext();
  const { showAddPreference, showManagePreferences } =
    useDataTableSettingsContext();

  return (
    <>
      <DropdownMenu variant="primary">
        <DropdownMenuTrigger
          asChild={true}
          disabled={
            table.tableCaption.showRefetch && table.dataQuery.isRefetching
          }
        >
          {trigger}
        </DropdownMenuTrigger>

        <DropdownMenuContent
          onCloseAutoFocus={(e) => {
            if (showAddPreference || showManagePreferences) {
              e.preventDefault();
            }
          }}
        >
          {children}

          <Preferences />
        </DropdownMenuContent>
      </DropdownMenu>

      {/*
       * TODO:
       * Autofocus in Dialog does not work properly when used inside DropdownMenu.
       * Sometimes focus stays on DropdownMenuItem or other DropdownMenu component.
       */}
      <AddDataTablePreferenceAction />

      <DataTablePreferencesEvidence />
    </>
  );
}
